import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { ModalChangePassword } from "@components/modules/users/change-password/ModalChangePassword";
import { Avatar } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import type { UserProfile } from "@src/__generated__/graphql";
import type { User } from "@src/__generated__/urql-graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { ModalSettingsSmallScreen } from "@src/components/widgets/Modals/ModalSettings/SmallScreen";
import { logout } from "@src/helpers/logout";
import type { UserType } from "@src/stores/models/Me";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import { preventDefault } from "@src/utils/preventDefault";
import { observer } from "mobx-react-lite";
import { Fragment } from "react";
import type { DeepPick } from "ts-essentials";
import packageJson from "../../../../../package.json";

interface LoggedUserProps {
  user: {
    id: User["id"];
    type: UserType;
    full_name: User["full_name"];
    profile?: Pick<UserProfile, "hex_color"> | null;
    image?: DeepPick<User["image"], { urls: { thumbnail: never } }>;
  };
}

const LoggedUser = ({ user }: LoggedUserProps) => {
  const settingsSmallScreenModal = useDisclosure();
  const changePasswordModal = useDisclosure();
  const { UIStore, settingsModalStore, editUserModalStore } = useStore();
  const { isDesktop } = useScreenType();

  const onLogout = preventDefault(() => {
    logout().then((res) => {
      if (res.ok) {
        location.reload();
      } else {
        UIStore.toast({
          title: t`Logging out failed. Please try again.`,
          status: "error",
        });
      }
    });
  });

  return (
    <Fragment>
      <Menu placement="bottom-end">
        <MenuButton
          as={Button}
          h="10"
          fontSize="xs"
          leftIcon={
            <Avatar
              name={user.full_name}
              src={user.image?.urls?.thumbnail}
              colorScheme={user.profile?.hex_color}
            />
          }
          variant="ghost"
        >
          <Icon name="chevron-down" w="4" h="4" color="grey.400" />
        </MenuButton>
        <Portal>
          <MenuList>
            <MenuItem
              icon={<Icon name="user-circle" w="5" h="5" color="grey.500" />}
              onClick={() => {
                editUserModalStore.modalState.onOpen({
                  userId: user.id,
                  userType: user.type,
                });
              }}
            >
              <Trans>My profile</Trans>
            </MenuItem>
            {can("workspaceSetting_edit") && (
              <MenuItem
                icon={<Icon name="settings-01" w="5" h="5" color="grey.500" />}
                onClick={() =>
                  isDesktop
                    ? settingsModalStore.modalState.onOpen()
                    : settingsSmallScreenModal.onOpen()
                }
              >
                <Trans>Settings</Trans>
              </MenuItem>
            )}
            <MenuItem
              icon={<Icon name="key-01" w="5" h="5" color="grey.500" />}
              onClick={changePasswordModal.onOpen}
            >
              <Trans>Change password</Trans>
            </MenuItem>
            <MenuItem
              icon={<Icon name="log-out-01" w="5" h="5" color="grey.500" />}
              onClick={onLogout}
            >
              <Trans>Log out</Trans>
            </MenuItem>
            {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
            <Text px="4" color="gray" fontSize="2xs" textAlign="end">
              v{packageJson.version}
            </Text>
          </MenuList>
        </Portal>
      </Menu>

      <ModalSettingsSmallScreen {...settingsSmallScreenModal} />
      <ModalChangePassword {...changePasswordModal} />
    </Fragment>
  );
};

export default observer(LoggedUser);
